import { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { Socio } from '../models/socioPrincipal.model';
import { YsocialContext } from '../contexts/YsocialContext';
import { Direccion } from '../interfaces/direccion.interface';
import { normalizarDireccion } from '../helpers/direccionHelper';
import { updateSocioPrincipal } from '../api/socioPrincipalService';
import { AsistenciaMedicaContext } from '../contexts/AsistenciaMedicaContext';
import { YsocialActionType } from '../interfaces/global-state/ysocial.action.type.enum';
import { toDireccionANormalizar } from '../interfaces/merlin/direccionANormalizar.interface';
import { CABA, CAPITAL, CORREGIDO, DUDOSO, NO_ENCONTRADO, NO_PARSEADO } from '../utils/constants';
import { AsistenciaMedicaActionType } from '../interfaces/global-state/asistencia.medica.action.type.enum';
import { formatDirectionNormalizada, completeDirection, validarDatosDireccionNormalizada } from '../utils/ubicacionUtils';
import { ResponseMerlin } from '../models/responseMerlin';

interface UseNormalizacionDireccionProps {
    socioPrincipal: Socio;
    apiRequest: (request: any) => Promise<any>;
    direccionesSocio: Direccion[] 
    isYsocial?: boolean,
    isGeo?: boolean,
    redirectTo?: string,
    propsRedirectTo?: {}
}

export const useDirecciones = ({
    socioPrincipal, 
    apiRequest, 
    direccionesSocio, 
    isYsocial,
    isGeo,
    redirectTo = null,
    propsRedirectTo = {}
}: UseNormalizacionDireccionProps) => {
    const history = useHistory();
    const { dispatchYsocial } = useContext(YsocialContext);
    const { dispatchAsistenciaMedica } = useContext(AsistenciaMedicaContext);
    const [ stateMerlin, setStateMerlin ] = useState<any>(undefined);
    const [ direccionInputState, setDireccionInputState ] = useState<Direccion | null>(null);
    const [ edicionDireccion, setEdicionDireccion ] = useState<boolean>(false);
    const [ merlinWaiting, setMerlinWaiting ] = useState<boolean>(false);
    const [ showNormalizacionModal, setShowNormalizacionModal ] = useState(false);
    const [ direccionesSocioAux, setDireccionesSocioAux ] = useState(direccionesSocio);

    const foundSelected = (direcciones: Direccion[]) => direcciones.find((address) => address.seleccionado === true) ?? null;

    const onCloseFormularioDomicilio = () => {
        if (redirectTo) {
            history.replace(redirectTo, propsRedirectTo);
        } else {
            history.goBack();
        } 
    }

    /*
     * Metodo que agrega al estado de direcciones del socio principal la nueva direccion
     */
    const addDirection = (newDireccion: Direccion) => {
        setEdicionDireccion(false);
        setMerlinWaiting(true);
        setDireccionInputState(newDireccion);
        normalizarDireccion(toDireccionANormalizar(newDireccion), apiRequest)
            .then((responseMerlin) => {
                setStateMerlin(responseMerlin);
                setMerlinWaiting(false);
                if (responseMerlin.status === CORREGIDO ) {
                    const { direccionNormalizada } = responseMerlin;
                    // Si pertenece a CABA Y los barrios no coinciden, abrir modal
                    if(validarDatosDireccionNormalizada(direccionNormalizada, newDireccion)) {
                        setShowNormalizacionModal(true);
                    } else {
                        saveNewDirection(responseMerlin,newDireccion);
                        //Cerrar modal de normalizacion y formulario de domicilio
                        setShowNormalizacionModal(false);
                        onCloseFormularioDomicilio();
                    }
                } else {
                    setShowNormalizacionModal(true);
                }
            })
            .catch((error) => {
                console.error(error);
                saveNewDirection(null, newDireccion);
                addErrorMerlin(newDireccion);
            });
    };

    /*
     * Metodo que modifica al estado de direcciones del socio principal con la direccion modificada
     */
    const modifyDirection = (direccionModificada: Direccion) => {
        setEdicionDireccion(true); // booleano utilizado en el modal para saber si se quiere agregar o editar una direccion
        setMerlinWaiting(true);
        setDireccionInputState(direccionModificada);
        normalizarDireccion(toDireccionANormalizar(direccionModificada), apiRequest) // verificar direccion ingresada con Merlin
            .then((responseMerlin) => {
                setStateMerlin(responseMerlin);
                setMerlinWaiting(false);
                if (responseMerlin.status === CORREGIDO) {
                    const { direccionNormalizada } = responseMerlin;                    
                    if(validarDatosDireccionNormalizada(direccionNormalizada, direccionModificada)){
                        setShowNormalizacionModal(true);
                    }else {
                        saveEditedDirection(responseMerlin, direccionModificada);
                        setShowNormalizacionModal(false);
                        onCloseFormularioDomicilio();
                    }
                }else{
                    setShowNormalizacionModal(true);
                }
            })
            .catch((error) => {
                console.error(error);
                saveEditedDirection(null, direccionModificada);
                modifyErrorMerlin(direccionModificada)
            });
    };

    /*
     * Metodo para editar y guardar la direccion en el socio
     */
    const saveEditedDirection = (responseMerlin: ResponseMerlin, direccionModificada: Direccion) => {
        if(responseMerlin === null){
            //Si coincide el id de la direccion a editar con alguna de las direcciones del array 
            //la reemplaza con direccionModificada que es la direccion nueva
            let direcciones = direccionesSocio.map(direccion => {
				return direccion.id === direccionModificada.id ? direccionModificada : direccion;
			})
            setDireccionesSocioAux(direcciones);
			updateDirection(direcciones);
        } else {
            //formatear la direccion de merlin al formato que se almacena en la base de datos
            let direccionFormateada = formatDirectionNormalizada(responseMerlin.direccionNormalizada);
            //agregar a direccionFormateada los atributos departamento, observaciones, piso, provincia, y seleccionado en true
            direccionFormateada = completeDirection(direccionFormateada, direccionModificada);
            //se agrega al nuevo objeto de direccion el id de la direccion que se desea modificiar
            direccionFormateada.id = direccionModificada.id;
            //se busca la direccion en las direcciones del socio 
            //y se la reemplaza con la direccion que se desea modificar con la direccion modificada
            let direcciones = direccionesSocioAux.map(direccion => {
                return direccion.id === direccionFormateada.id ? direccionFormateada : direccion;
            })
            setDireccionesSocioAux(direcciones);
            updateDirection(direcciones);
        }
    }

    /*
     * Metodo para guardar la direccion en el socio
     */
    const saveNewDirection = ( responseMerlin: ResponseMerlin, newDireccion: Direccion ) => {
        let direccionesAux: Direccion[] = direccionesSocioAux;
        //se cambia el estado seleccionado a false a cada una de las direcciones del socio
        direccionesAux.forEach(direccion => {
            direccion.seleccionado = false
        });
        if(responseMerlin === null) {
            //se agrega la nueva direccion que tiene estado seleccionado en true
			direccionesAux.push(newDireccion);
			updateDirection(direccionesAux);
        } else {
            //formatear la direccion de merlin al formato que se almacena en la base de datos
            let direccionFormateada = formatDirectionNormalizada(responseMerlin.direccionNormalizada);
            //agregar a direccionFormateada los atributos departamento, observaciones, piso, provincia, y seleccionado en true
            direccionFormateada = completeDirection(direccionFormateada, newDireccion);
            
            //se agrega la nueva direccion que tiene estado seleccionado en true
            direccionesAux.push(direccionFormateada);
            updateDirection(direccionesAux);
        }
    }

    /*
     * Metodo que se encarga de normalizar la direccion ingresada por el formulario utilizando la api de Merlin
     * solo cuando los estados son distintos a CO 
     */
    const handleNormalizacion = (data: any) => {
        switch(stateMerlin.status) {
            case DUDOSO:
                normalizarDireccion({
                    barrio: direccionInputState?.barrio || '',
                    calle: data.calle,
                    localidad: direccionInputState?.localidad || '',
                    altura: direccionInputState?.altura || '',
                    provincia: direccionInputState?.provincia || '',
                }, apiRequest)
                    .then((responseMerlin) => {
                        let direccionFormateada: Direccion = formatDirectionNormalizada(responseMerlin.direccionNormalizada);
                        direccionFormateada = completeDirection(direccionFormateada, direccionInputState as Direccion);
                        let direccionesAux: Direccion[] = direccionesSocioAux;
                        direccionesAux.forEach(direccion => {
                            direccion.seleccionado = false
                        });
                        direccionesAux.push(direccionFormateada);
                        updateDirection(direccionesAux);
                        setShowNormalizacionModal(false);
                        onCloseFormularioDomicilio();
                    });
                break;
            case NO_ENCONTRADO:
            case NO_PARSEADO:
                setShowNormalizacionModal(false);
                break;
            case CORREGIDO:
                saveNewDirection(stateMerlin,direccionInputState);
                setShowNormalizacionModal(false);
                onCloseFormularioDomicilio();
                break;
            default:
                setShowNormalizacionModal(false);
                break;
        }
    };

    /*
     * Metodo que se encarga de normalizar la direccion ingresada por el formulario utilizando la api de Merlin
     * solo cuando los estados son distintos a CO 
     */
    const handleNormalizacionEditar = (data: any) => {
        switch(stateMerlin.status) {
            case DUDOSO:
                normalizarDireccion({
                    barrio: direccionInputState?.barrio,
                    calle: data?.calle,
                    localidad: direccionInputState?.localidad,
                    altura: direccionInputState?.altura,
                    provincia: direccionInputState?.provincia
                }, apiRequest)
                    .then((responseMerlin) => {
                        let direccionFormateada: Direccion = formatDirectionNormalizada(responseMerlin.direccionNormalizada);
                        direccionFormateada = completeDirection(direccionFormateada, direccionInputState);
                        direccionFormateada.id = direccionInputState.id;
                        let direcciones = direccionesSocioAux.map(direccion => {
                            return direccion.id === direccionFormateada.id ? direccionFormateada : direccion;
                        })
                        setDireccionesSocioAux(direcciones);
                        updateDirection(direcciones);
                        setShowNormalizacionModal(false);
                        onCloseFormularioDomicilio();
                    })
                    break;
            case NO_ENCONTRADO:
            case NO_PARSEADO:
                setShowNormalizacionModal(false);
                break;
            case CORREGIDO:
                saveEditedDirection(stateMerlin, direccionInputState);
                if(isGeo) {
                    let direccionFormateada = formatDirectionNormalizada(stateMerlin.direccionNormalizada);
                    direccionFormateada = completeDirection(direccionFormateada, direccionInputState);
                    direccionFormateada.id = direccionInputState.id;
                    let direcciones = direccionesSocioAux.map(direccion => {
                        return direccion.id === direccionFormateada.id ? direccionFormateada : direccion;
                    })
                    setDireccionesSocioAux(direcciones);
                    updateDirection(direcciones);
                }
                setShowNormalizacionModal(false);
                onCloseFormularioDomicilio();
                break;
            default:
                setShowNormalizacionModal(false);
                break;
        }
    }
    
    const updateDirection = (direcciones: Direccion[]) => {
        
        //TODO: refactorizar las lineas en comun
        
        if(isYsocial) {
            let socio = socioPrincipal;
            socio.direcciones = direcciones;
            updateSocioPrincipal(socioPrincipal, apiRequest)
                .then(response => {
                    dispatchYsocial({
                        type: YsocialActionType.actualizarDireccion,
                        direcciones: response.data.direcciones
                    });
                    dispatchYsocial({
                        type: YsocialActionType.actualizarDireccionSeleccionada,
                        direccionSeleccionada: foundSelected(socio.direcciones)
                    })
                })
                .catch(error => {
                    console.error(`Hubo un error al persistir el socio principal ${error}`);
                });
            //ver el state y navegar
            setShowNormalizacionModal(false);
        } else {
            let socio: Socio = socioPrincipal;
            let direccionesAux = direcciones.map((direc) => {
                if (direc.provincia === CABA) {
                    return ({ ...direc, provincia: CAPITAL })
                }
                return direc;
            })
            socio.direcciones = direccionesAux;
            dispatchAsistenciaMedica({
                type: AsistenciaMedicaActionType.actualizarDireccionSeleccionada,
                direccionSeleccionada: foundSelected(socio.direcciones)
            })
            updateSocioPrincipal(socioPrincipal, apiRequest)
                .then(response => {
                    dispatchAsistenciaMedica({
                        type: AsistenciaMedicaActionType.actualizarDireccion,
                        direcciones: response.data.direcciones
                    })
                    for (let i = 0; i < direcciones.length; i++) {
                        direcciones[i] = { ...direcciones[i], id: response.data.direcciones[i].id }
                        direcciones[i].socioId = socioPrincipal.id;
                    }
                    setDireccionesSocioAux(direcciones);
                })
                .catch(error => {
                    console.error(`Hubo un error al persistir el socio principal ${error}`);
                }
            )
        }
    };

    //cuando omitis
    const handleCloseModal = (setSelectedGeolocalization?: React.Dispatch<React.SetStateAction<boolean>>) => {
        if (stateMerlin.status === DUDOSO) {
			setShowNormalizacionModal(false);
		} else {
            saveNewDirection(null, direccionInputState);
            setShowNormalizacionModal(false);
            onCloseFormularioDomicilio();
		}
    }

    //cuando omitis editando
    const handleCloseModalEdit = (setSelectedGeolocalization?: React.Dispatch<React.SetStateAction<boolean>>) => {
        if (stateMerlin.status === DUDOSO) {
            setShowNormalizacionModal(false);
        } else {
            // direccionesSocioAux
            direccionInputState.posicionExacta = false;
            direccionInputState.entreCalleUno = '';
            direccionInputState.entreCalleDos = '';
            direccionInputState.latitud = 0;
            direccionInputState.longitud = 0;
            saveEditedDirection(null, direccionInputState);
            setShowNormalizacionModal(false);
            onCloseFormularioDomicilio();
        }
    }

    const addErrorMerlin = ( newDireccion: Direccion ) => {
        let direccionesAux: Direccion[] = direccionesSocioAux;
        direccionesAux.forEach(direccion => {
            direccion.seleccionado = false
        });
        direccionesAux.push(newDireccion);
        updateDirection(direccionesAux);
        onCloseFormularioDomicilio();
        setMerlinWaiting(false);
    }

    const modifyErrorMerlin = (direccionModificada: Direccion) => {
        let direcciones = direccionesSocioAux.map(direccion => {
            return direccion.id === direccionModificada.id ? direccionModificada : direccion;
        })
        setDireccionesSocioAux(direcciones);
        updateDirection(direcciones);
        setShowNormalizacionModal(false);
        onCloseFormularioDomicilio();
        setMerlinWaiting(false);
    }

    return {
        stateMerlin,
        direccionInputState,
        edicionDireccion,
        merlinWaiting,
        modifyDirection,
        addDirection,
        saveNewDirection,
        handleNormalizacion,
        showNormalizacionModal,
        setShowNormalizacionModal,
        updateDirection,
        handleNormalizacionEditar,
        handleCloseModal,
        handleCloseModalEdit,
        foundSelected
    };
};
